import React, {useMemo} from 'react';
import type {List} from 'immutable';

import {Button, Icon, Text} from '@albert-io/atomic';
import type {ClassroomModelV1} from '@albert-io/models';
import InvitedStudentsActionsDropdown from 'client/Classrooms/Classroom/Teacher/ManageRoster/InvitedStudents/InvitedStudentsActionsDropdown/InvitedStudentsActionsDropdown.react';

import {InviteMenu} from '../InviteMenu';
import {InviteMethodCards} from './InviteMethodCards';
import {PeopleEmptyState} from './PeopleEmptyState';
import {RosterTable} from './RosterTable';
import type {RosterColumn} from './RosterTable';
import {useClientSort} from './useClientSort';
import type {RosterClassroom, RosterInvitation, RosterStudent} from './usePeopleRoster';

// sent_email_at can be null from a race; fall back to updated_at (mirrors the legacy column).
const invitedDate = (invitation: RosterInvitation) =>
  invitation.getSentEmailAt() ?? invitation.getUpdatedAt() ?? null;

// ISO for chronological sort; the cell renders the friendly 'LL' form.
const getSortValue = (invitation: RosterInvitation, key: string): string => {
  if (key === 'email') {
    return invitation.getEmail();
  }
  return invitedDate(invitation)?.format('YYYY-MM-DD') ?? '';
};

interface Props {
  classroom: RosterClassroom;
  students: List<RosterStudent>;
  invitations: List<RosterInvitation>;
  search: string;
  onShowModal: (modal: string) => void;
}

export const InvitedList = ({classroom, students, invitations, search, onShowModal}: Props) => {
  const filtered = useMemo(() => {
    const all = invitations.toArray();
    const term = search.trim().toLowerCase();
    if (!term) {
      return all;
    }
    return all.filter((invitation) => invitation.getEmail().toLowerCase().includes(term));
  }, [invitations, search]);

  const {sorted, sortKey, sortDir, toggleSort} = useClientSort(
    filtered,
    getSortValue,
    'lastInvited',
    'desc'
  );

  if (invitations.isEmpty()) {
    if (students.isEmpty()) {
      return <InviteMethodCards onShowModal={onShowModal} />;
    }
    return (
      <PeopleEmptyState
        action={
          <InviteMenu
            classroom={classroom as unknown as ClassroomModelV1}
            classroomId={classroom.getId()}
            trigger={(triggerProps) => (
              <Button {...triggerProps} roundness='pill'>
                <Icon className='mr-x1' icon='user-plus' iconStyle='regular' />
                Invite Students
              </Button>
            )}
          />
        }
        body='All invitations were accepted.'
        icon='envelope'
        title='No invitations'
      />
    );
  }

  const columns: RosterColumn<RosterInvitation>[] = [
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      cellClassName: 'px-x2 py-x1-half break-all',
      render: (invitation) => <Text>{invitation.getEmail()}</Text>
    },
    {
      key: 'lastInvited',
      label: 'Last invited',
      sortable: true,
      cellClassName: 'px-x2 py-x1-half',
      render: (invitation) => <Text>{invitedDate(invitation)?.format('LL') ?? '—'}</Text>
    },
    {
      key: 'actions',
      headerClassName: 'w-x6',
      srOnlyHeader: 'Actions',
      cellClassName: 'px-x2 py-x1-half text-right',
      render: (invitation) => <InvitedStudentsActionsDropdown invitation={invitation} />
    }
  ];

  return (
    <RosterTable
      ariaLabel='Invited students'
      columns={columns}
      getRowKey={(invitation) => invitation.getId()}
      noResultsMessage='No invitations match your search.'
      onSort={toggleSort}
      rows={sorted}
      sortDir={sortDir}
      sortKey={sortKey}
    />
  );
};
